import React from 'react';
import { Lock } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A browser, drawn rather than embedded.
 *
 * It shows the address the vendor is typing, so the field on the left has an
 * immediate consequence on the right — the thing being talked about appears as
 * it is named. An iframe would be the literal answer and the wrong one: most
 * sites refuse to be framed, it would fire a request on every keystroke, and a
 * half-loaded stranger's page is not a nice thing to put on a first screen.
 *
 * The page beneath the chrome is deliberately abstract and fades out before it
 * ends, so it reads as a suggestion of a website rather than a claim about
 * theirs.
 */
export const BrowserFrame: React.FC<{ url?: string; screenshot?: string; className?: string }> = ({
    url,
    screenshot,
    className,
}) => {
    const display = (url || '').trim().replace(/^https?:\/\//i, '') || 'yourbusiness.com';

    return (
        <div
            aria-hidden
            className={cn(
                'pointer-events-none w-full overflow-hidden rounded-xl border bg-card select-none',
                className,
            )}
        >
            {/* Chrome */}
            <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2.5">
                <div className="flex shrink-0 gap-1.5">
                    <span className="size-2 rounded-full bg-border" />
                    <span className="size-2 rounded-full bg-border" />
                    <span className="size-2 rounded-full bg-border" />
                </div>

                <div className="ms-1 flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-background px-2 py-1">
                    <Lock className="size-3 shrink-0 text-muted-foreground" />
                    {/* Keyed on the value so a changed address fades rather than snapping. */}
                    <span
                        key={display}
                        className={cn(
                            'min-w-0 flex-1 animate-in truncate text-[11px] fade-in duration-300',
                            url ? 'text-foreground' : 'text-muted-foreground',
                        )}
                    >
                        {display}
                    </span>
                </div>
            </div>

            {/*
              The page. Shapes until the capture lands, then the real thing.

              Swapped in place rather than shown somewhere else on the screen:
              the vendor typed an address into this frame, so this is where
              their site belongs when it arrives. Only the top of it — the mask
              dissolves the rest, which keeps it a glimpse rather than a claim
              to be reading the whole page.
            */}
            <div
                className="relative h-56 overflow-hidden"
                style={{
                    maskImage: 'linear-gradient(to bottom, black 0%, black 38%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 38%, transparent 100%)',
                }}
            >
                {screenshot ? (
                    <img
                        key={screenshot}
                        src={screenshot}
                        alt=""
                        className="w-full animate-in object-cover object-top duration-700 fade-in"
                    />
                ) : (
                    <div className="px-5 pt-5">
                        <div className="mb-4 flex items-center justify-between">
                            <div className="h-2.5 w-20 rounded-full bg-foreground/15" />
                            <div className="flex gap-2">
                                <div className="h-2 w-10 rounded-full bg-foreground/10" />
                                <div className="h-2 w-10 rounded-full bg-foreground/10" />
                                <div className="h-2 w-10 rounded-full bg-foreground/10" />
                            </div>
                        </div>

                        <div className="h-24 w-full rounded-lg bg-foreground/[0.07]" />

                        <div className="mt-4 grid grid-cols-3 gap-3">
                            <div className="h-16 rounded-lg bg-foreground/[0.06]" />
                            <div className="h-16 rounded-lg bg-foreground/[0.06]" />
                            <div className="h-16 rounded-lg bg-foreground/[0.06]" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
